"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { MdMenu, MdClose } from "react-icons/md";
import { trackEvent } from "@/lib/analytics";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const isBlogPage = pathname?.startsWith("/blog");
  const isTeamPage = pathname?.startsWith("/team");

  const scrollToSection = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string,
  ) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    if (pathname !== "/") {
      router.push(`/#${sectionId}`);
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };


  const navLinks = (
    <>
      <a
        href="#features"
        onClick={(e) => scrollToSection(e, "features")}
        className="text-sm font-medium text-primary-color hover:text-primary-color/70 transition-colors cursor-pointer"
      >
        Features
      </a>
      <Link
        href="/trust"
        className="text-sm font-medium text-primary-color hover:text-primary-color/70 transition-colors cursor-pointer"
      >
        Trust
      </Link>
      <Link
        href="/team"
        className={`text-sm font-medium transition-colors cursor-pointer py-1 rounded-[13px] ${
          isTeamPage
            ? "bg-[#EDF0FF] text-primary-color w-16 text-center"
            : "text-primary-color hover:text-primary-color/70"
        }`}
      >
        Team
      </Link>
    </>
  );

  const navLinksRight = (
    <>
      <Link
        href="/blog"
        className={`text-sm font-medium transition-colors cursor-pointer py-1 rounded-[13px] ${
          isBlogPage
            ? "bg-[#EDF0FF] text-primary-color w-16 text-center"
            : "text-primary-color hover:text-primary-color/70"
        }`}
      >
        Blog
      </Link>
      <a
        href="https://calendly.com/lauren-logical/logical-health-intro-call"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("cta_click", { cta: "book_a_demo_nav" })}
        className="text-sm text-white cursor-pointer font-bold whitespace-nowrap border bg-coverage-alert rounded-4xl w-32 h-8 text-center py-1.5"
      >
        Book a Demo
      </a>
    </>
  );

  return (
    <nav
      className={"fixed z-50 left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out w-full md:w-2xl md:mt-[17px] top-0 md:top-[17px]"}
    >
      {/* Desktop navigation */}
      <div className="site-nav-surface md:rounded-full px-4 md:px-6 py-2 flex items-center justify-between md:justify-center md:gap-6">
        {/* Mobile hamburger */}
        <button
          className="md:hidden text-primary-color p-1"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <MdClose size={22} /> : <MdMenu size={22} />}
        </button>

        {/* Left nav links (desktop) */}
        <div className="hidden md:flex items-center gap-8">{navLinks}</div>

        {/* Center logo */}
        <Link href="/" className="flex items-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/Logotype_Horizontal_Blue_Light_BG.svg"
            alt="Logical Health"
            className="h-8 md:h-auto w-auto"
          />
        </Link>

        {/* Right nav links (desktop) */}
        <div className="hidden md:flex items-center gap-8">{navLinksRight}</div>

        {/* Empty spacer for mobile alignment */}
        <div className="w-8 md:hidden" />
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white h-screen px-11 pt-28 relative">
          <div
            className="absolute left-0 bottom-0 right-0 h-[239px]"
            style={{
              backgroundImage: "url('/mobile-navbar-dropdown-bg-pattern.png')",
              backgroundSize: "auto 100%",
              backgroundPosition: "bottom right",
              backgroundRepeat: "repeat-x",
            }}
          />
          <div className="flex flex-col gap-4 text-center text-2xl font-normal text-primary-color">
            <a href="#features" onClick={(e) => scrollToSection(e, "features")}>
              Features
            </a>
            <Link href="/trust" onClick={() => setMobileMenuOpen(false)}>
              Trust
            </Link>
            <Link href="/team" onClick={() => setMobileMenuOpen(false)}>
              Team
            </Link>
            <Link href="/blog" onClick={() => setMobileMenuOpen(false)}>
              Blog
            </Link>
            <a
              href="https://calendly.com/lauren-logical/logical-health-intro-call"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("cta_click", { cta: "book_a_demo_mobile_menu" })}
              className="text-2xl font-bold text-white w-[300px] border bg-coverage-alert text-center py-4 h-16 rounded-4xl mt-28"
            >
              Book a Demo
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
